import { faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import GoogleMapReact from 'google-map-react';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { MaskService } from 'react-native-masked-text';
import Toast from 'react-native-toast-message';
import { OverlappingMarkerSpiderfier } from 'ts-overlapping-marker-spiderfier';
import { cameraService } from '../services/central-api/cameras';
import { ONLINE_AGENT_TIME_MINUTES, TeamNameCategory } from '../services/central-api/dispatch';
import { translate } from '../services/translate';
import { WindowInformation } from '../services/window-information';
import MyAppText from './MyAppText';
import FormInput from './FormInput';
import { platformSettingsService } from '../services/central-api/platform-settings';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import Container from './Container';
import CustomButton from './CustomButton';
import { QueryObserverResult } from '@tanstack/react-query';

interface MarkerVehiclePos {
    id: number;
    marker: google.maps.Marker;
}

export type MapMouseEvent = google.maps.MapMouseEvent & { domEvent: MouseEvent; };

interface MapProperties {
    dynamicMarker?: LatLng;
    useDynamicMarker?: boolean;
    markerAsPoliceStation?: boolean;
    pinnedMarker?: LatLng;
    dynamicPosition?: LatLng;
    markerChanged?: (pos: LatLng) => void;
    pinClick?: (marker: google.maps.Marker, event: MapMouseEvent) => void;
    cameraPositions?: CameraMap[];
    agentPositions?: UnitMapPosition[];
    policeStations?: PoliceStation[];
    occurrencePositions?: OccurrencePosition[];
    pointsOfInterestPositions?: PointOfInterest[];
    polygons?: MapPolygon[];
    draggable?: boolean;
    mapControls?: boolean;
    onClick?: (pos: LatLng) => void;
    refreshPointsOfInterest?: () => Promise<QueryObserverResult<PointOfInterest[], unknown>>;
    canManagePointsOfInterest?: boolean;
    showMapPoi?: boolean;
    shouldOmitUnitsAndAgents?: boolean;
    radius?: number;
    alwaysFitBoundsForCameraPositions?: boolean;
    onStreetViewChanged?: (isOpen: boolean) => void;
}

export default function Map({
    dynamicMarker,
    useDynamicMarker,
    markerAsPoliceStation,
    pinnedMarker,
    dynamicPosition,
    markerChanged,
    pinClick,
    cameraPositions,
    agentPositions,
    policeStations,
    occurrencePositions,
    pointsOfInterestPositions,
    polygons,
    draggable = true,
    mapControls = true,
    onClick,
    refreshPointsOfInterest,
    canManagePointsOfInterest,
    showMapPoi = false,
    shouldOmitUnitsAndAgents = true,
    radius,
    alwaysFitBoundsForCameraPositions = false,
    onStreetViewChanged,
}: MapProperties) {
    const { styles, theme } = useStyles(styleSheet);
    const [map, setMap] = useState<google.maps.Map>();
    const [maps] = useState<typeof google.maps>(window?.google?.maps);
    const [marker, setMarker] = useState<Marker>();
    const [infoWindow] = useState(new maps.InfoWindow());
    const [bounds, setBounds] = useState<google.maps.LatLngBounds>();
    const [markerAgentList] = useState<MarkerVehiclePos[]>([]);
    const [reloadBounds, setReloadBounds] = useState<boolean>(false);
    const [isFirstPoiLoad, setIsFirstPoiLoad] = useState<boolean>(true);
    const [isFirstCameraLoad, setIsFirstCameraLoad] = useState<boolean>(true);
    const [isFirstAgentLoad, setIsFirstAgentLoad] = useState<boolean>(true);
    const [isFirstOccurrenceLoad, setIsFirstOccurrenceLoad] = useState<boolean>(true);
    const [isFirstPoliceStationLoad, setIsFirstPoliceStationLoad] = useState<boolean>(true);
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [cameraMarkerClusterer, setCameraMarkerClusterer] = useState<MarkerClusterer>();
    const [pointOfInterestMarkerClusterer, setPointOfInterestMarkerClusterer] = useState<MarkerClusterer>();
    const [occurrencesMarkerClusterer, setOccurrencesMarkerClusterer] = useState<MarkerClusterer>();
    const [agentMarkerClusterer, setAgentMarkerClusterer] = useState<MarkerClusterer>();
    const [policeStationClusterer, setPoliceStationClusterer] = useState<MarkerClusterer>();
    const [cameraMarkerSpiderfier, setCameraMarkerSpiderfier] = useState<OverlappingMarkerSpiderfier>();
    const [pointOfInterestId, setPointOfInterestId] = useState<number>(0);
    const [pointOfInterestName, setPointOfInterestName] = useState<string>('');
    const [pointOfInterestDescription, setPointOfInterestDescription] = useState<string | undefined>('');
    const [pointOfInterestLatitude, setPointOfInterestLatitude] = useState<number>();
    const [pointOfInterestLongitude, setPointOfInterestLongitude] = useState<number>();
    const [drawnPolygons, setDrawPolygons] = useState<{
        type: string;
        polygons: google.maps.Polygon[];
        infoWindows: google.maps.InfoWindow[];
    }>({
        type: '',
        polygons: [],
        infoWindows: []
    });
    const [defaultZoom, setDefaultZoom] = useState<number>(11);
    const [defaultCenter, setDefaultCenter] = useState<GoogleMapReact.Coords | undefined>();
    const loaders = useRef({
        didAgentsLoad: false,
        didOccurrencesLoad: false,
        didPoliceStationsLoad: false,
        didCamerasLoad: false,
        didPointsOfInterestLoad: false,
    });
    const circleRef = useRef<google.maps.Circle | null>(null);
    const { data } = platformSettingsService.useMapCentralPoint();
    const streetViewRef = useRef(false);

    const windowInfo = WindowInformation();

    function castToInt(value: string | number): number {
        if (typeof value === 'number') {
            return value;
        }
        if (value) {
            value = value.replace(',', '.');
        }
        return Number(value);
    }

    function getPoliceStationIcon() {
        let iconName: string = '';
        if (UnistylesRuntime.themeName == 'dark') {
            iconName = 'Pin-Dark-Delegacia';
        } else {
            iconName = 'Pin-Light-Delegacia';
        }
        return require(`../../assets/${iconName}.svg`);
    }

    function getOccurrenceIcon() {
        let iconName: string = '';
        if (UnistylesRuntime.themeName == 'dark') {
            iconName = 'Pin-Dark-Ocorrencia';
        } else {
            iconName = 'Pin-Light-Ocorrencia';
        }
        return require(`../../assets/${iconName}.svg`);
    }

    function getDynamicMarkerIcon() {
        return markerAsPoliceStation
            ? {
                url: markerAsPoliceStation ? getPoliceStationIcon() : '',
                scaledSize: new maps.Size(42, 42),
            } : '';
    }

    function agentPositionIcon(category: string) {
        const categoryIcon = ['light_vehicle', 'heavy_vehicle'].includes(category) ? 'Viatura' : category == 'motorcycle' ? 'Moto' : 'Policial';

        let themeName: string = '';
        if (UnistylesRuntime.themeName == 'dark') {
            themeName = 'Dark';
        } else {
            themeName = 'Light';
        }

        return require(`../../assets/Pin-${themeName}-${categoryIcon}.svg`);
    }

    function manageAgentPositionsLoad() {
        try {
            if (!loaders.current.didAgentsLoad) {
                loaders.current.didAgentsLoad = true;
                return;
            }
            if (!map || !maps || agentPositions === undefined || !bounds || !agentMarkerClusterer) {
                return;
            }

            const localMarkers = [];
            for (const agentPosition of agentPositions) {
                if (agentPosition.latitude && agentPosition.longitude && (moment(agentPosition.positionUpdatedAt).isAfter(moment().subtract(ONLINE_AGENT_TIME_MINUTES, 'minutes')) || !shouldOmitUnitsAndAgents)) {
                    const markerRef = markerAgentList.find((obj: MarkerVehiclePos) => {
                        return obj.id === agentPosition.id;
                    });
                    if (markerRef) {
                        markerRef.marker.setPosition({
                            lat: castToInt(agentPosition.latitude),
                            lng: castToInt(agentPosition.longitude)
                        });
                        localMarkers.push(markerRef.marker);
                    } else {

                        const marker = new maps.Marker({
                            map,
                            position: {
                                lat: castToInt(agentPosition.latitude),
                                lng: castToInt(agentPosition.longitude)
                            },
                            icon: {
                                url: agentPositionIcon(agentPosition.user.unit?.unitName.category),
                                scaledSize: new maps.Size(30, 30)
                            }
                        });

                        const pos = marker.getPosition();
                        if (pos) {
                            bounds.extend(pos);
                        }

                        if (agentPosition.id) {
                            let content = '';
                            const maskedValue = MaskService.toMask('cel-phone', agentPosition.user.phone ?? '', {
                                maskType: 'BRL',
                                withDDD: true,
                                dddMask: '(99) '
                            });

                            if (agentPosition.user.unit) {
                                if ([TeamNameCategory.LIGHT_VEHICLE, TeamNameCategory.HEAVY_VEHICLE, TeamNameCategory.MOTORCYCLE]
                                    .includes(agentPosition.user.unit.unitName.category)) {
                                    content += `${translate('team')}: ${agentPosition.user.unit.unitName.name}<br/>`;
                                    content += `${maskedValue}<br/>`;
                                } else if (agentPosition.user.warName || agentPosition.user.name) {
                                    content += `${agentPosition.user.warName || agentPosition.user.name} - ${maskedValue}<br/>`;
                                    content += `${translate('team')}: ${agentPosition.user.unit.unitName.name}<br/>`;
                                }
                            } else {
                                content += `${agentPosition.user.warName || agentPosition.user.name} - ${maskedValue}<br/>`;
                            }

                            if (agentPosition.positionUpdatedAt) {
                                content += `<br/>${translate('lastUpdate')}:</br>`;
                                content += moment(agentPosition.positionUpdatedAt).format('DD/MM/YYYY HH:mm:ss');
                            }

                            maps.event.addListener(marker, 'click', function (event: MapMouseEvent) {
                                if (pinClick) {
                                    pinClick(marker, event);
                                }
                                infoWindow.close();
                                infoWindow.setContent(content);
                                infoWindow.open(map, marker);
                            });

                            markerAgentList.push({ id: agentPosition.id, marker: marker });
                            localMarkers.push(marker);
                        }
                    }
                }
            }

            agentMarkerClusterer.clearMarkers();
            agentMarkerClusterer.addMarkers(localMarkers);

            if (isFirstAgentLoad && localMarkers.length) {
                setIsFirstAgentLoad(false);
                setReloadBounds(!reloadBounds);
            }
        } catch (e) {
            console.error(e);
        }
    }

    function manageOccurrencesLoad() {
        try {
            if (!loaders.current.didOccurrencesLoad) {
                loaders.current.didOccurrencesLoad = true;
                return;
            }
            if (!map || !maps || occurrencePositions === undefined || !bounds || !occurrencesMarkerClusterer) {
                return;
            }

            const localMarkers = [];
            for (const occurrence of occurrencePositions) {
                if (occurrence.latitude && occurrence.longitude) {
                    const marker = new maps.Marker({
                        map,
                        position: {
                            lat: castToInt(occurrence.latitude),
                            lng: castToInt(occurrence.longitude)
                        },
                        icon: {
                            url: getOccurrenceIcon(),
                            scaledSize: new maps.Size(30, 30)
                        }
                    });

                    maps.event.addListener(marker, 'click', function (event: MapMouseEvent) {
                        if (pinClick) {
                            pinClick(marker, event);
                        }
                        infoWindow.close();
                        infoWindow.setContent(String(occurrence?.id));
                        infoWindow.open(map, marker);
                    });

                    const pos = marker.getPosition();
                    if (pos) {
                        bounds.extend(pos);
                    }
                    localMarkers.push(marker);
                }
            }

            occurrencesMarkerClusterer.clearMarkers();
            occurrencesMarkerClusterer.addMarkers(localMarkers);

            if (isFirstOccurrenceLoad && localMarkers.length) {
                setIsFirstOccurrenceLoad(false);
                setReloadBounds(!reloadBounds);
            }
        } catch (e) {
            console.error(e);
        }

    }

    function managePoliceStationsLoad() {
        try {
            if (!loaders.current.didPoliceStationsLoad) {
                loaders.current.didPoliceStationsLoad = true;
                return;
            }
            if (!map || !maps || policeStations === undefined || !bounds || !policeStationClusterer) {
                return;
            }

            const localMarkers = [];
            for (const policeStation of policeStations) {
                if (policeStation.latitude && policeStation.longitude) {
                    const marker = new maps.Marker({
                        map,
                        position: {
                            lat: castToInt(policeStation.latitude),
                            lng: castToInt(policeStation.longitude)
                        },
                        icon: {
                            url: getPoliceStationIcon(),
                            scaledSize: new maps.Size(30, 30)
                        }
                    });

                    maps.event.addListener(marker, 'click', function (event: MapMouseEvent) {
                        if (pinClick) {
                            pinClick(marker, event);
                        }
                        infoWindow.close();
                        infoWindow.setContent(String(policeStation?.name));
                        infoWindow.open(map, marker);
                    });

                    const pos = marker.getPosition();
                    if (pos) {
                        bounds.extend(pos);
                    }
                    localMarkers.push(marker);
                }
            }

            policeStationClusterer.clearMarkers();
            policeStationClusterer.addMarkers(localMarkers);

            if (isFirstPoliceStationLoad && localMarkers.length) {
                setIsFirstPoliceStationLoad(false);
                setReloadBounds(!reloadBounds);
            }
        } catch (e) {
            console.error(e);
        }
    }

    function managePointsOfInterestLoad() {
        try {
            if (!loaders.current.didPointsOfInterestLoad) {
                loaders.current.didPointsOfInterestLoad = true;
                return;
            }
            if (!map || !maps || pointsOfInterestPositions === undefined || !pointOfInterestMarkerClusterer || !bounds) {
                return;
            }

            const localMarkers = [];
            for (const pointOfInterest of pointsOfInterestPositions) {
                const marker: Marker = new maps.Marker({
                    map,
                    position: {
                        lat: castToInt(pointOfInterest.latitude),
                        lng: castToInt(pointOfInterest.longitude)
                    },
                    icon: {
                        url: require('../../assets/pin_point_of_interest.svg'),
                        scaledSize: new maps.Size(30, 30)
                    },
                    draggable
                });

                maps.event.addListener(marker, 'click', function () {
                    setModalVisible(false);
                    setTimeout(() => {
                        setPointOfInterestId(pointOfInterest.id);
                        setPointOfInterestName(pointOfInterest.name);
                        setPointOfInterestDescription(pointOfInterest.description);
                        setModalVisible(true);
                    }, 200);
                });

                maps.event.addListener(marker, 'dragend', function () {
                    const pos = marker.getPosition();
                    setPointOfInterestId(pointOfInterest.id);
                    setPointOfInterestName(pointOfInterest.name);
                    setPointOfInterestDescription(pointOfInterest.description);
                    if (pos) {
                        setPointOfInterestLatitude(pos.lat());
                        setPointOfInterestLongitude(pos.lng());
                    }
                    setModalVisible(true);
                });

                const pos = marker.getPosition();
                if (pos) {
                    bounds.extend(pos);
                }
                localMarkers.push(marker);
            }
            pointOfInterestMarkerClusterer.clearMarkers();
            pointOfInterestMarkerClusterer.addMarkers(localMarkers);

            if (isFirstPoiLoad && localMarkers.length) {
                setIsFirstPoiLoad(false);
                setReloadBounds(!reloadBounds);
            }
        } catch (e) {
            console.error(e);
        }

    }

    function manageCamerasLoad() {
        try {
            if (!loaders.current.didCamerasLoad) {
                loaders.current.didCamerasLoad = true;
                return;
            }
            if (!map || !maps || cameraPositions === undefined || !cameraMarkerClusterer || !bounds) {
                return;
            }

            const localMarkers = [];
            for (const camera of cameraPositions) {
                if (camera.latitude && camera.longitude) {
                    const marker: Marker = new maps.Marker({
                        map,
                        position: {
                            lat: castToInt(camera.latitude),
                            lng: castToInt(camera.longitude)
                        },
                        icon: {
                            url: camera.thirdPartyCamera || camera.originalCameraId ?
                                require(`../../assets/${camera.type}_third_party.svg`) :
                                require(`../../assets/${camera.type}.svg`),
                            scaledSize: new maps.Size(50, 50)
                        },
                    });
                    marker.metadata = { camera };
                    localMarkers.push(marker);

                    if (cameraMarkerSpiderfier) {
                        maps.event.addListener(marker, 'spider_click', function (event: MapMouseEvent) {
                            const isModifierClick = event.domEvent.button === 1 || event.domEvent.ctrlKey || event.domEvent.metaKey;

                            if (pinClick && !isModifierClick) {
                                pinClick(marker, event);
                            }
                        });

                        maps.event.addListener(marker, 'mousedown', function (event: MapMouseEvent) {
                            const isModifierClick = event.domEvent.button === 1 || event.domEvent.ctrlKey || event.domEvent.metaKey;

                            if (pinClick && isModifierClick) {
                                pinClick(marker, event);

                                event.domEvent.preventDefault();
                                event.domEvent.stopPropagation();
                            }
                        });

                        cameraMarkerSpiderfier.addMarker(marker, () => { });
                    }

                    const pos = marker.getPosition();

                    if (pos) {
                        bounds.extend(pos);
                    }
                }
            }

            if (alwaysFitBoundsForCameraPositions) {
                setTimeout(() => {
                    if (bounds) {
                        map.fitBounds(bounds, -5);
                    }
                }, 50);
            }

            cameraMarkerClusterer.clearMarkers();
            cameraMarkerClusterer.addMarkers(localMarkers);

            if (isFirstCameraLoad && localMarkers.length) {
                setIsFirstCameraLoad(false);
                setReloadBounds(!reloadBounds);
            }
        } catch (e) {
            console.error(e);
        }
    }

    function manageDynamicMarkerLoad() {
        try {
            if (!map || !maps || !useDynamicMarker) {
                return;
            }

            const newMarker = dynamicMarker || defaultCenter;
            if (!newMarker) {
                return;
            }

            if (!marker) {
                const mapMarker = new maps.Marker({
                    map: map,
                    icon: getDynamicMarkerIcon(),
                    position: new maps.LatLng(newMarker.lat, newMarker.lng),
                    draggable
                });

                maps.event.addListener(mapMarker, 'dragend', function () {

                    const pos = mapMarker.getPosition();

                    if (markerChanged && pos) {
                        markerChanged({ lat: pos.lat(), lng: pos.lng() });
                    }
                });
                setMarker(mapMarker);
            } else {
                const latLng = new maps.LatLng(newMarker.lat, newMarker.lng);
                marker.setPosition(latLng);
            }
            if (marker) {
                const pos = marker.getPosition();
                if (pos) {
                    map.setCenter(pos);
                }
                map.setZoom(16);
            }

        } catch (e) {
            console.error(e);
        }
    }

    function managePinnedMarkerLoad() {
        try {
            if (!map || !maps || !pinnedMarker) {
                return;
            }

            if (!marker) {
                const mapMarker = new maps.Marker({
                    map: map,
                    position: new maps.LatLng(pinnedMarker.lat, pinnedMarker.lng),
                });
                setMarker(mapMarker);
            } else {
                const latLng = new maps.LatLng(pinnedMarker.lat, pinnedMarker.lng);
                marker.setPosition(latLng);
            }

            map.setCenter(pinnedMarker);
            map.setZoom(16);
        } catch (e) {
            console.error(e);
        }
    }

    function drawPolygons() {
        try {
            if (!map || !maps || !polygons) {
                return;
            }

            let type = '';

            if (polygons[0]?.type) {
                type = polygons[0].type;
            }

            if (type === drawnPolygons.type) {
                return;
            }

            // will ensure all the infoWindows are closed
            for (const mapPolInfoWindow of drawnPolygons.infoWindows) {
                mapPolInfoWindow.close();
            }
            // will remove any drawn polygon on the map
            for (const polygon of drawnPolygons.polygons) {
                polygon.setMap(null);
            }

            const mapPolygons: google.maps.Polygon[] = [];
            const polygonsInfoWindow: google.maps.InfoWindow[] = [];
            for (const polygon of polygons) {
                const mapPolygon = new maps.Polygon({
                    paths: polygon.points,
                    strokeColor: polygon.color,
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: polygon.color,
                    fillOpacity: 0.35,
                });
                mapPolygon.setMap(map);
                mapPolygons.push(mapPolygon);
                const mapPolygonInfoWindow = new maps.InfoWindow({
                    content: `${polygon.name}<br/>`
                });
                polygonsInfoWindow.push(mapPolygonInfoWindow);
                mapPolygon.addListener('click', function (event: google.maps.MapMouseEvent) {
                    mapPolygonInfoWindow.setPosition(event.latLng);
                    mapPolygonInfoWindow.open(map);
                });

            }
            setDrawPolygons({ type, polygons: mapPolygons, infoWindows: polygonsInfoWindow });
        } catch (e) {
            console.error(e);
        }
    }

    function manageDynamicPositionChange() {
        if (map && dynamicPosition) {
            map.setCenter(dynamicPosition);
            map.setZoom(16);
        }
    }

    function drawRadius() {
        if (!map || !maps || !pinnedMarker || !radius) {
            return;
        }

        if (circleRef.current) {
            circleRef.current.setMap(null);
        }

        const circle = new maps.Circle({
            strokeColor: '#3399FF',
            strokeOpacity: 0.7,
            strokeWeight: 2,
            fillColor: '#3399FF',
            fillOpacity: 0.2,
            map,
            center: { lat: pinnedMarker.lat, lng: pinnedMarker.lng },
            radius: radius * 1000,
        });
        circleRef.current = circle;

        setTimeout(() => {
            const bounds = circle.getBounds();
            if (bounds) {
                map.fitBounds(bounds, -5);
            }
        }, 50);
    }

    useEffect(() => {
        if (data) {
            setDefaultCenter({ lat: data.latitude, lng: data.longitude });
            setDefaultZoom(data.zoom);
        }
    }, [data]);

    useEffect(managePoliceStationsLoad, [map, maps, policeStations, isFirstPoliceStationLoad, reloadBounds, bounds, loaders, policeStationClusterer, theme]);
    useEffect(manageAgentPositionsLoad, [map, maps, agentPositions, isFirstAgentLoad, reloadBounds, bounds, loaders, agentMarkerClusterer, markerAgentList, theme]);
    useEffect(manageOccurrencesLoad, [map, maps, occurrencePositions, isFirstOccurrenceLoad, reloadBounds, bounds, loaders, occurrencesMarkerClusterer, theme]);
    useEffect(managePointsOfInterestLoad, [map, maps, pointsOfInterestPositions, isFirstPoiLoad, reloadBounds, bounds, loaders, pointOfInterestMarkerClusterer]);
    useEffect(manageCamerasLoad, [map, maps, cameraPositions, isFirstCameraLoad, reloadBounds, bounds, loaders, cameraMarkerClusterer, cameraMarkerSpiderfier]);
    useEffect(manageDynamicMarkerLoad, [dynamicMarker, map, maps, useDynamicMarker]);
    useEffect(managePinnedMarkerLoad, [pinnedMarker, map, maps]);
    useEffect(drawRadius, [pinnedMarker, map, maps, radius]);
    useEffect(drawPolygons, [drawnPolygons, polygons, map, maps]);
    useEffect(manageDynamicPositionChange, [dynamicPosition, map]);

    useEffect(() => {
        if (!map || !bounds) {
            return;
        }
        if (!defaultCenter) {
            map.fitBounds(bounds);
        }
    }, [reloadBounds, bounds, map, defaultCenter]);

    useEffect(() => {
        if (!map || !marker) {
            return;
        }

        const pos = marker.getPosition();
        if (pos) {
            map.setCenter(pos);
        }
    }, [marker]);

    useEffect(() => {
        if (marker) {
            marker.setDraggable(draggable);
        }
    }, [draggable]);

    return (
        <Container>
            {defaultCenter &&
                <GoogleMapReact
                    center={defaultCenter}
                    draggable={draggable}
                    defaultZoom={defaultZoom}
                    onClick={onClick}
                    options={() => ({
                        streetViewControl: mapControls,
                        fullscreenControl: false,
                        zoomControl: mapControls,
                        styles: UnistylesRuntime.themeName === 'light' || UnistylesRuntime.themeName == 'lightAlt' ? [{
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [showMapPoi ? {} : { visibility: 'off' }],
                        },
                        {
                            featureType: 'transit',
                            stylers: [{ visibility: 'off' }],
                        }] : [
                            { featureType: 'poi', stylers: [showMapPoi ? {} : { visibility: 'off' }] },
                            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                            {
                                featureType: 'administrative.locality',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#d59563' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'geometry',
                                stylers: [{ color: '#38414e' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'geometry.stroke',
                                stylers: [{ color: '#212a37' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#9ca5b3' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'geometry',
                                stylers: [{ color: '#746855' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'geometry.stroke',
                                stylers: [{ color: '#1f2835' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#f3d19c' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'geometry',
                                stylers: [{ color: '#17263c' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#515c6d' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'labels.text.stroke',
                                stylers: [{ color: '#17263c' }],
                            },
                        ]
                    })}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({ map, maps }: { map: google.maps.Map; maps: typeof google.maps; }) => {
                        setBounds(new maps.LatLngBounds());
                        setMap(map);
                        setCameraMarkerClusterer(new MarkerClusterer({
                            map,
                            markers: [],
                            algorithm: new SuperClusterAlgorithm({ minPoints: 2 }),
                        }));
                        setPointOfInterestMarkerClusterer(new MarkerClusterer({
                            map,
                            markers: [],
                        }));
                        setOccurrencesMarkerClusterer(new MarkerClusterer({
                            map,
                            markers: [],
                            algorithm: new SuperClusterAlgorithm({ minPoints: 2 }),
                        }));
                        setCameraMarkerSpiderfier(new OverlappingMarkerSpiderfier(map, {
                            legWeight: 8,
                            markersWontMove: true,
                            basicFormatEvents: true
                        }));
                        setAgentMarkerClusterer(new MarkerClusterer({
                            map,
                            markers: [],
                            algorithm: new SuperClusterAlgorithm({ minPoints: 2 }),
                        }));
                        setPoliceStationClusterer(new MarkerClusterer({
                            map,
                            markers: [],
                            algorithm: new SuperClusterAlgorithm({ minPoints: 2 }),
                        }));
                        const panorama = map.getStreetView();
                        panorama.addListener('visible_changed', () => {
                            const isVisible = panorama.getVisible();
                            if (streetViewRef.current !== isVisible) {
                                streetViewRef.current = isVisible;
                                onStreetViewChanged?.(isVisible);
                            }
                        });
                    }}>
                </GoogleMapReact>
            }
            {pointsOfInterestPositions ?
                <Modal
                    transparent={true}
                    visible={isModalVisible}
                    animationType='slide'
                    onRequestClose={() => {
                        setModalVisible(false);
                    }}
                >
                    <View style={styles.modalContainer}>
                        <View style={styles.centeredView}>
                            <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : {}]}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <MyAppText style={{ fontSize: 22, fontWeight: 'bold', color: theme.colors.labelColor }}>{translate('pointOfInterest')}</MyAppText>
                                    <TouchableOpacity onPress={() => setModalVisible(false)}>
                                        <FontAwesomeIcon icon={faClose} fontSize={20} style={{ color: theme.colors.labelColor }} />
                                    </TouchableOpacity>
                                </View>
                                <View style={{ gap: 5 }}>
                                    <FormInput
                                        label={translate('name')}
                                        invalid={() => false}
                                        disabled={!canManagePointsOfInterest}
                                        value={pointOfInterestName}
                                        onChangeText={setPointOfInterestName}
                                    />
                                    <FormInput
                                        numberOfLines={!canManagePointsOfInterest ? 5 : 3}
                                        multiline={true}
                                        label={translate('description')}
                                        disabled={!canManagePointsOfInterest}
                                        invalid={() => false}
                                        value={pointOfInterestDescription || ''}
                                        onChangeText={setPointOfInterestDescription}
                                    />
                                </View>
                                {canManagePointsOfInterest ?
                                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', columnGap: 5 }}>
                                        <CustomButton
                                            onPress={async () => {
                                                try {
                                                    await cameraService.deletePointOfInterest(pointOfInterestId);
                                                    if (refreshPointsOfInterest) {
                                                        await refreshPointsOfInterest();
                                                    }

                                                    Toast.show({
                                                        type: 'sentinelxSuccess',
                                                        text1: translate('ActionSuccessfully'),
                                                    });
                                                } catch (error) {
                                                    Toast.show({
                                                        type: 'sentinelxError',
                                                        text1: translate('unexpectedError'),
                                                    });
                                                } finally {
                                                    setModalVisible(false);
                                                }
                                            }}
                                            text={translate('delete')}
                                            type='destructive'
                                        />
                                        <CustomButton
                                            onPress={async () => {
                                                try {
                                                    if (pointOfInterestLatitude && pointOfInterestLongitude) {
                                                        await cameraService.updatePointOfInterest({
                                                            id: pointOfInterestId,
                                                            name: pointOfInterestName,
                                                            description: pointOfInterestDescription,
                                                            latitude: pointOfInterestLatitude,
                                                            longitude: pointOfInterestLongitude
                                                        });
                                                    }
                                                    if (refreshPointsOfInterest) {
                                                        await refreshPointsOfInterest();
                                                    }
                                                    Toast.show({
                                                        type: 'sentinelxSuccess',
                                                        text1: translate('ActionSuccessfully'),
                                                    });
                                                } catch (error) {
                                                    Toast.show({
                                                        type: 'sentinelxError',
                                                        text1: translate('unexpectedError'),
                                                    });
                                                } finally {
                                                    setModalVisible(false);
                                                }
                                            }}
                                            text={translate('save')}
                                            type='primary'
                                        />
                                    </View> : null}
                            </View>
                        </View>
                    </View>
                </Modal> : null}
        </Container >
    );
}


const styleSheet = createStyleSheet((theme) => ({
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    formContainer: {
        minWidth: 350,
        maxWidth: 500,
        backgroundColor: theme.colors.card.background,
        borderRadius: 2,
        padding: 20,
        minHeight: 200,
        rowGap: 30
    },
}));
